import { FieldOptionBase, FieldTypeDisplay } from 'module/api/field/types';
//import { byAssigneeValue } from 'module/helpers/comparators/by-assignee-value';
import { byFullName } from 'module/helpers/comparators/by-full-name';
import { byLabel } from 'module/helpers/comparators/by-label';
import { fullName } from 'module/helpers/full-name';
import { getRandomColor } from 'module/helpers/get-random-color';
import { tryGetTextFromHTML } from 'module/helpers/getTextFromHTML';
//import { unreachable } from 'module/helpers/unreachable';
import { FieldType } from 'module/types/filter-field/field/field';
import { FilterableField } from 'module/types/filter-field/field/filterable-field';
//import { AssigneeValue } from 'module/types/filter-field/field/filterable-field';
import { NoValue } from 'module/ui/components/atoms/no-value/empty-cell';
import { RichTextEditorInput } from 'module/ui/components/atoms/rich-text-editor-input/editor';
import { SelectFieldOption } from 'module/ui/components/molecules/field-editable-value/option';
import { ICellRendererParams } from 'ag-grid-community';
import { Checkbox, Tooltip } from 'antd';
import { isString, memoize } from 'lodash';
//import { useGetAllActiveNativeUsersQuery } from 'queries/member';
import { FC } from 'react';
import styled from 'styled-components';
import { UserTag } from '../../atoms/select/select';
import type {
  FieldBases as Field,
  UserFieldMetadata,
  UserOption,
} from 'module/types/filter-field/field/filterable-field';

const NoteWrapper = styled.div`
  width: 100%;
  cursor: pointer;
  height: inherit;
  display: flex;
  align-items: center;
  --rte-min-height: 0;
  --rte-content-padding: var(--spacing-02) var(--spacing-03) var(--spacing-03) 0;
  --rte-background: transparent;
  .ck.ck-content.ck-editor__editable.ck-editor__editable_inline {
    max-height: var(--height-lg);
    white-space: pre-wrap;
    line-height: var(--spacing-07);
    overflow: hidden;
  }
  .ck.ck-content.ck-editor__editable.ck-editor__editable_inline.ck-read-only {
    padding: var(--rte-content-padding);
  }
`;

export const NoteFieldRenderer: FC<
  React.PropsWithChildren<
    ICellRendererParams & {
      field: FilterableField;
      openNote?: (field: FilterableField, data) => void;
    }
  >
> = ({ openNote, field, data, value }) => {
  return (
    <NoteWrapper onClick={() => openNote?.(field, data)}>
      {value == null || !isString(value) ? (
        <NoValue />
      ) : (
        <RichTextEditorInput value={value?.replaceAll('\n', '<br>')} readonly />
      )}
    </NoteWrapper>
  );
};

type Nullable<T> = T | null;

const renderMultiLineTextValue = memoize((value: Nullable<string>) => {
  if (value == null) {
    return <NoValue />;
  }

  const text = tryGetTextFromHTML(value);

  return (
    <Tooltip
      placement="bottomRight"
      title={text}
      overlayStyle={{
        maxHeight: '256px',
        maxWidth: '336px',
        overflowY: 'auto',
      }}
    >
      <div
        className="multiline-text"
        style={{
          marginTop: '10px',
        }}
        dangerouslySetInnerHTML={{
          __html: value,
        }}
      />
    </Tooltip>
  );
});

type NewField = FilterableField &
  Pick<
    Field,
    | 'fieldDataType'
    | 'fieldType'
    | 'optionsAvailable'
    | 'isEnabled'
    | 'isEditable'
  >;

export const getRenderer = (
  field: Pick<
    NewField,
    | 'type'
    | 'fieldName'
    | 'metaData'
    | 'sourceType'
    | 'isEditable'
    | 'fieldTypeDisplay'
  >,
) => {
  const { type } = field;

  const options = field?.metaData?.options ?? ([] as FieldOptionBase[]);

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-constraint
  const getOption = memoize(<T extends unknown>(value: Nullable<T>) =>
    options.find((o) => o.value === value),
  );

  const renderBoolean = memoize((value: Nullable<boolean>, disabled = false) =>
    value === null ? (
      <NoValue />
    ) : (
      <Checkbox checked={value} disabled={disabled} />
    ),
  );

  const renderMultiSelect = memoize(
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-constraint
    <T extends unknown>(value: Nullable<T[]>) => {
      const selected = value ?? [];

      return selected.length === 0 ? (
        <NoValue />
      ) : (
        <span className="spacer">
          {selected
            .map(getOption)
            .filter(Boolean)
            .map((x) => x!)
            .sort(byLabel)
            .map((x) => renderOption(x.value))}
        </span>
      );
    },
    (value: Nullable<unknown[]>) =>
      Array.from(new Set(value ?? []))
        .sort()
        .map((x) => x?.toString())
        .join(','),
  );
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-constraint
  const renderOption = memoize(<T extends unknown>(value: Nullable<T>) => {
    const selected = getOption(value);

    if (selected == null) {
      return <NoValue />;
    }

    return (
      <SelectFieldOption
        key={selected.label}
        option={selected}
        dataCy={`tag.${field.fieldName}.${selected.label}`}
        showPosition={field.fieldTypeDisplay === FieldTypeDisplay.Level}
      />
    );
  });

  const renderUser = memoize((value: Nullable<number>) => {
    if (value == null) {
      return <NoValue />;
    }

    const selected = field?.metaData?.options?.find(
      (option) => option.value === value,
    ) as UserOption;

    if (selected == null) {
      return <NoValue />;
    }

    //console.log('Selected', selected);

    return (
      <UserTag
        key={selected?.value}
        color={getRandomColor(selected?.value ?? 0)}
        imageUrl={selected?.imageUrl}
        userStatus={selected?.extra?.userStatus}
        label={selected?.label}
      />
    );
  });

  const renderMultiUser = memoize(
    (value: Nullable<number[]>) => {
      if ((value ?? []).length === 0) {
        return <NoValue />;
      }

      const fieldMetadata = field?.metaData as UserFieldMetadata;

      const userIds = fieldMetadata?.users?.by_user_id;

      const users = Object.values(userIds).filter((user) =>
        value?.includes(user.userId),
      );

      //console.log('Users', users);

      const html = users
        .sort(byFullName)
        .filter(({ userId }) => value!.includes(userId))
        .map((selected, idx) => (
          <UserTag
            key={selected?.userId ?? idx}
            color={getRandomColor(selected?.userId ?? 0)}
            imageUrl={selected?.photoUrl}
            label={fullName(selected ?? ({} as any))}
            userStatus={selected?.userStatus}
          />
        ));

      return <span className="spacer">{html} </span>;
    },
    (value: Nullable<number[]>) =>
      Array.from(new Set(value ?? []))
        .sort()
        .map((x) => x.toString())
        .join(','),
  );

  /*const toOption = memoize(
    (value: AssigneeValue) => {
      if (value == null) {
        return null;
      }

      switch (value[0]) {
        case 'team': {
          const team = (teams ?? []).find((x) => x.teamId === value[1]);

          return team == null ? null : TeamOption(team);
        }
        case 'user': {
          const user = users?.find((u) => u.userId === value[1]);

          return user == null ? null : UserOption(user);
        }
        default:
          return unreachable(value);
      }
    },
    (value: AssigneeValue) => value.join(' '),
  );

  const renderAssignees = memoize(
    (value: Nullable<AssigneeValue[]>) => {
      if (value == null || (value ?? []).length === 0) {
        return <NoValue />;
      }

      const html = value
        .map(toOption)
        .filter(Boolean)
        .sort(byLabel)
        .filter((selected) => selected?.extra != null)
        .map((selected) =>
          selected.extra!.type === 'user'
            ? renderUser(selected.extra!.id)
            : renderTeam(selected.extra!.id),
        );

      return <span className="spacer">{html}</span>;
    },
    (value: Nullable<AssigneeValue[]>) =>
      (value ?? [])
        .filter(Boolean)
        .sort(byAssigneeValue)
        .map(AssigneeValue.toString)
        .join(','),
  ); */

  switch (type) {
    case FieldType.Estimate:
    case FieldType.Number:
    case FieldType.Date:
    case FieldType.Text:
      return undefined;
    case FieldType.Boolean:
      return ({ value }: { value: Nullable<boolean> }) =>
        renderBoolean(value, field.isEditable);
    case FieldType.SingleSelect:
      return ({ value }: { value: Nullable<number> }) => renderOption(value);
    case FieldType.MultiSelect:
      return ({ value }: { value: Nullable<number[]> }) =>
        renderMultiSelect(value);
    case FieldType.MultiUser:
      return ({ value }: { value: Nullable<number[]> }) => {
        return renderMultiUser(value);
      };
    case FieldType.SingleUser:
      return ({ value }: { value: Nullable<number> }) => renderUser(value);

    case FieldType.MultiLineText:
      return ({ value }: { value: Nullable<string> }) =>
        renderMultiLineTextValue(value);
    case FieldType.Note:
      return 'NoteFieldRenderer';
    default:
      return 'Unreached code';
  }
};
